import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import './HRManualAttendance.css';

const BASE = 'https://fotileams.com/api';

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
  };
}

function toLocalDateStr(date) {
  return date.toISOString().split('T')[0];
}

function formatTimeForInput(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}

function getInitials(name) {
  if (!name) return '??';
  const p = name.trim().split(' ');
  return (p[0][0] + (p[p.length-1][0] || '')).toUpperCase();
}

function getAvatarColor(name) {
  const colors = ['#6366f1','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4'];
  let h = 0;
  for (let i = 0; i < (name||'').length; i++) h += name.charCodeAt(i);
  return colors[h % colors.length];
}

// ── Leave types ───────────────────────────────────────────
// These are the only three that deduct from an employee's balance.
// Selecting one writes an APPROVED leaves row on the server, so the
// balance drops everywhere (Leaves page, Salary, Reports). Switching the
// day back to any non-leave status deletes that row and refunds the day.
const LEAVE_TYPES = [
  { value: 'casual', label: 'Casual Leave', icon: '📋' },
  { value: 'sick',   label: 'Sick Leave',   icon: '🤒' },
  { value: 'annual', label: 'Annual Leave', icon: '🌴' },
];

const STATUS_OPTIONS = [
  { value: 'present',               label: '✓ Present' },
  { value: 'minor_late',            label: '⏱ Minor Late' },
  { value: 'major_late',            label: '⏱ Major Late' },
  { value: 'half_day',              label: '½ Half Day' },
  { value: 'absent',                label: '✗ Absent' },
  { value: 'approved_leave:casual', label: '📋 Casual Leave' },
  { value: 'approved_leave:sick',   label: '🤒 Sick Leave' },
  { value: 'approved_leave:annual', label: '🌴 Annual Leave' },
  { value: 'public_holiday',        label: '🎉 Public Holiday' },
];

// casual_auto is deliberately NOT offered here — it's a system status the
// nightly reconcile job assigns when an absence is auto-covered by casual.
// HR marking it by hand would consume a casual day with no leaves row
// behind it. Existing casual_auto records still display correctly below.

const LEAVE_PREFIX = 'approved_leave:';

// Split the composite dropdown value into what the API expects.
function splitStatusValue(value) {
  if (!value) return { status: 'present', leaveType: null, isLeave: false };
  if (value.startsWith(LEAVE_PREFIX)) {
    const type = value.slice(LEAVE_PREFIX.length);
    if (LEAVE_TYPES.some(l => l.value === type)) {
      return { status: 'approved_leave', leaveType: type, isLeave: true };
    }
    // Legacy row: approved_leave with no/unknown type. Not a valid choice —
    // HR must pick a real type before saving.
    return { status: 'approved_leave', leaveType: null, isLeave: true };
  }
  return { status: value, leaveType: null, isLeave: false };
}

// Pick the dropdown value that matches an existing attendance record.
function initialStatusValue(record) {
  if (!record) return 'present';
  if (record.status === 'approved_leave') {
    const t = (record.leave_type || '').toLowerCase();
    if (LEAVE_TYPES.some(l => l.value === t)) return `${LEAVE_PREFIX}${t}`;
    return `${LEAVE_PREFIX}__legacy`;   // approved_leave with no type recorded
  }
  if (record.status === 'casual_auto') return 'casual_auto';
  return record.status || 'present';
}

// ── Edit Modal ────────────────────────────────────────────
function EditModal({ employee, record, date, onSave, onClose }) {
  const [clockIn,  setClockIn]  = useState(record ? formatTimeForInput(record.clock_in)  : '');
  const [clockOut, setClockOut] = useState(record ? formatTimeForInput(record.clock_out) : '');
  const [status,   setStatus]   = useState(initialStatusValue(record));
  const [notes,    setNotes]    = useState(record?.notes  || '');
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');

  const [balance,    setBalance]    = useState(null);
  const [balLoading, setBalLoading] = useState(true);
  const [balError,   setBalError]   = useState('');

  const { status: statusPart, leaveType, isLeave } = splitStatusValue(status);

  // The leave type already saved on this record, if any.
  const originalLeaveType = record?.status === 'approved_leave'
    ? (record.leave_type || '').toLowerCase()
    : null;

  // ── Fetch this employee's leave balance ────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBalLoading(true); setBalError('');
      try {
        const year = new Date(`${date}T00:00:00`).getFullYear();
        const res  = await fetch(
          `${BASE}/leaves/balance/employee/${employee.id}?year=${year}`,
          { headers: authHeaders() }
        );
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok) {
          setBalError(data.error || 'Could not load leave balance');
          setBalance(null);
        } else {
          setBalance(data.balance || null);
        }
      } catch {
        if (!cancelled) { setBalError('Could not load leave balance'); setBalance(null); }
      } finally {
        if (!cancelled) setBalLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [employee.id, date]);

  // Re-saving the SAME leave type that's already on the record doesn't
  // deduct a second day, so it must not be blocked by a low balance.
  const isSameAsOriginal = isLeave && leaveType && originalLeaveType === leaveType;

  const remainingForSelected = (isLeave && leaveType && balance)
    ? Number(balance[leaveType]?.remaining ?? 0)
    : null;

  // Legacy approved_leave row with no type — force HR to choose one.
  const needsLeaveType = isLeave && !leaveType;

  // HARD BLOCK: no days of that type left.
  const blockedNoBalance =
    isLeave && leaveType && !isSameAsOriginal &&
    balance !== null && remainingForSelected < 1;

  // Switching a leave day back to something else refunds the day.
  const willRefund = !!originalLeaveType && !isLeave;

  const saveDisabled = saving || blockedNoBalance || needsLeaveType;

  const handleSave = async () => {
    if (saveDisabled) return;
    setSaving(true); setError('');
    try {
      // Build datetime strings combining date + time input
      const buildDT = (timeStr) => {
        if (!timeStr) return null;
        return `${date}T${timeStr}:00+05:00`; // PKT
      };

      // A leave day has no clock times by definition — the server clears
      // them too, this just keeps the request honest.
      const clockInDT  = isLeave ? null : buildDT(clockIn);
      const clockOutDT = isLeave ? null : buildDT(clockOut);

      const payload = {
        clock_in:   clockInDT,
        clock_out:  clockOutDT,
        status:     statusPart,
        leave_type: isLeave ? leaveType : null,
        notes,
      };

      let res;
      if (record?.id) {
        // Update existing record
        res = await fetch(`${BASE}/attendance/${record.id}/override`, {
          method: 'PUT', headers: authHeaders(),
          body: JSON.stringify(payload),
        });
      } else {
        // Create new record
        res = await fetch(`${BASE}/attendance/manual`, {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({ employee_id: employee.id, date, ...payload }),
        });
      }

      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to save'); return; }
      onSave(data);
    } catch { setError('Server error — try again'); }
    finally { setSaving(false); }
  };

  return (
    <div className="ma-modal-overlay" onClick={onClose}>
      <div className="ma-modal" onClick={e => e.stopPropagation()}>
        <div className="ma-modal-header">
          <div className="ma-modal-emp">
            <div className="ma-modal-av" style={{ background: getAvatarColor(employee.full_name) }}>
              {getInitials(employee.full_name)}
            </div>
            <div>
              <div className="ma-modal-name">{employee.full_name}</div>
              <div className="ma-modal-meta">{employee.employee_id} · {date}</div>
            </div>
          </div>
          <button className="ma-modal-close" onClick={onClose}>✕</button>
        </div>

        {error && <div className="ma-modal-error">{error}</div>}

        <div className="ma-modal-body">
          {/* ── Leave balance strip ───────────────────────────────── */}
          <div className="ma-bal-strip">
            <div className="ma-bal-title">Leave balance remaining</div>
            {balLoading ? (
              <div className="ma-bal-loading">Loading balance…</div>
            ) : balError ? (
              <div className="ma-bal-warn">
                ⚠️ {balError} — the server will still block the save if there's
                no balance left.
              </div>
            ) : balance ? (
              <>
                <div className="ma-bal-pills">
                  {LEAVE_TYPES.map(lt => {
                    const b   = balance[lt.value] || {};
                    const rem = Number(b.remaining ?? 0);
                    const sel = leaveType === lt.value;
                    return (
                      <div
                        key={lt.value}
                        className={`ma-bal-pill${rem < 1 ? ' ma-bal-empty' : ''}${sel ? ' ma-bal-sel' : ''}`}
                      >
                        <span className="ma-bal-ico">{lt.icon}</span>
                        <span className="ma-bal-lbl">{lt.label.replace(' Leave','')}</span>
                        <span className="ma-bal-num">{rem}</span>
                        <span className="ma-bal-tot">/ {b.total ?? '—'}</span>
                      </div>
                    );
                  })}
                </div>
                {balance._detail?.casual_days_lost_to_lates > 0 && (
                  <div className="ma-bal-note">
                    {balance._detail.casual_days_lost_to_lates} casual day(s) already
                    consumed by the late policy
                    {balance._detail.casual_auto_days > 0 &&
                      `, ${balance._detail.casual_auto_days} by auto-covered absence(s)`}.
                  </div>
                )}
              </>
            ) : null}
          </div>

          <div className="ma-field">
            <label>Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)}>
              {STATUS_OPTIONS.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
              {/* Only shown when the existing record needs them, so the
                  select never silently falls back to the wrong option. */}
              {status === `${LEAVE_PREFIX}__legacy` && (
                <option value={`${LEAVE_PREFIX}__legacy`}>
                  📋 Approved Leave (type not recorded)
                </option>
              )}
              {status === 'casual_auto' && (
                <option value="casual_auto">📋 Casual Leave (auto, system)</option>
              )}
            </select>
          </div>

          {needsLeaveType && (
            <div className="ma-inline-warn">
              This day is recorded as leave but no type was saved with it.
              Pick Casual, Sick or Annual to fix it — that will deduct one day
              from the chosen balance.
            </div>
          )}

          {blockedNoBalance && (
            <div className="ma-inline-block">
              🚫 No {leaveType} leave remaining
              <span className="ma-inline-block-sub">
                {employee.full_name} has 0 of {balance?.[leaveType]?.total ?? '—'} {leaveType} days
                left this year. Mark the day as Absent, or use a different leave type.
              </span>
            </div>
          )}

          {isSameAsOriginal && (
            <div className="ma-inline-info">
              Already marked as {leaveType} leave — saving again won't deduct
              another day.
            </div>
          )}

          {isLeave && !blockedNoBalance && !isSameAsOriginal && !needsLeaveType && (
            <div className="ma-inline-info">
              This will deduct <strong>1 {leaveType} day</strong>
              {typeof remainingForSelected === 'number' &&
                ` (${remainingForSelected} → ${remainingForSelected - 1} remaining)`}
              {originalLeaveType && ` and refund the ${originalLeaveType} day currently on this date`}.
            </div>
          )}

          {willRefund && (
            <div className="ma-inline-info">
              ↩️ This will remove the {originalLeaveType} leave on this date and
              refund <strong>1 {originalLeaveType} day</strong> to the balance.
            </div>
          )}

          <div className="ma-field-row">
            <div className="ma-field">
              <label>Clock In Time</label>
              <input type="time" value={isLeave ? '' : clockIn}
                disabled={isLeave}
                onChange={e => setClockIn(e.target.value)} />
            </div>
            <div className="ma-field">
              <label>Clock Out Time</label>
              <input type="time" value={isLeave ? '' : clockOut}
                disabled={isLeave}
                onChange={e => setClockOut(e.target.value)} />
            </div>
          </div>
          {isLeave && (
            <div className="ma-field-hint">
              Clock times don't apply to a leave day and will be cleared.
            </div>
          )}

          <div className="ma-field">
            <label>Notes (reason for manual entry)</label>
            <textarea rows={2} placeholder="e.g. Employee reported in but system issue"
              value={notes} onChange={e => setNotes(e.target.value)} />
          </div>
        </div>

        <div className="ma-modal-footer">
          <button className="ma-btn-cancel" onClick={onClose}>Cancel</button>
          <button className="ma-btn-save" onClick={handleSave} disabled={saveDisabled}>
            {saving ? 'Saving…' : record?.id ? 'Update Record' : 'Add Record'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────
export default function HRManualAttendance() {
  const [date, setDate]           = useState(toLocalDateStr(new Date()));
  const [employees, setEmployees] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [editData, setEditData]   = useState(null); // { employee, record }
  const [search, setSearch]       = useState('');
  const [success, setSuccess]     = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [empRes, attRes] = await Promise.all([
        api.getAllEmployees(),
        fetch(`${BASE}/attendance/?date=${date}`, { headers: authHeaders() }).then(r => r.json()),
      ]);
      setEmployees((empRes.employees || []).filter(e => e.role === 'employee'));
      setAttendance(attRes.records || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [date]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getRecord = (emp) => attendance.find(r => r.emp_code === emp.employee_id);

  const getStatusBadge = (rec) => {
    if (!rec) return { label: 'No Record', cls: 'ma-s-none' };
    const s = rec.status;
    if (s === 'present')        return { label: 'Present', cls: 'ma-s-present' };
    if (s === 'minor_late' || s === 'major_late' || s === 'half_day')
                                return { label: `Late ${rec.late_minutes}m`, cls: 'ma-s-late' };
    if (s === 'absent')         return { label: 'Absent', cls: 'ma-s-absent' };
    if (s === 'approved_leave') {
      const t    = (rec.leave_type || '').toLowerCase();
      const meta = LEAVE_TYPES.find(l => l.value === t);
      return {
        label: meta ? meta.label : 'Approved Leave',
        cls: 'ma-s-leave',
      };
    }
    if (s === 'casual_auto')    return { label: 'Casual (auto)', cls: 'ma-s-leave' };
    if (s === 'public_holiday') return { label: 'Holiday', cls: 'ma-s-holiday' };
    if (rec.clock_in)           return { label: 'Present', cls: 'ma-s-present' };
    return { label: 'No Record', cls: 'ma-s-none' };
  };

  const handleSave = (result) => {
    setEditData(null);
    let msg = 'Attendance updated successfully';
    if (result?.leave_created && result?.leave_type)
      msg = `Saved — 1 ${result.leave_type} day deducted from the balance`;
    else if (result?.leave_refunded)
      msg = 'Saved — the leave day was removed and refunded to the balance';
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 4000);
    fetchData();
  };

  const filtered = employees.filter(e =>
    e.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    e.employee_id?.toLowerCase().includes(search.toLowerCase())
  );

  const presentCount = attendance.filter(r => r.clock_in).length;
  const absentCount  = employees.filter(e => !getRecord(e)).length;
  const leaveCount   = attendance.filter(
    r => r.status === 'approved_leave' || r.status === 'casual_auto'
  ).length;

  return (
    <div className="ma-root">
      <div className="ma-header">
        <div>
          <h1 className="ma-title">⚠️ Manual Attendance</h1>
          <p className="ma-subtitle">Temporary feature — transition period only</p>
        </div>
        <div className="ma-header-right">
          <input type="date" className="ma-date-input"
            value={date} onChange={e => setDate(e.target.value)}
            max={toLocalDateStr(new Date())} />
        </div>
      </div>

      {/* Warning banner */}
      <div className="ma-warning-banner">
        ⚠️ Manual entries override automated tracking. Use only when necessary and always add a note explaining why.
        Marking a day as Casual, Sick or Annual leave deducts a day from that employee's balance.
      </div>

      {success && <div className="ma-success">{success}</div>}

      {/* Stats */}
      <div className="ma-stats">
        <div className="ma-stat ma-stat-green">
          <span className="ma-stat-num">{presentCount}</span>
          <span className="ma-stat-lbl">Present</span>
        </div>
        <div className="ma-stat ma-stat-red">
          <span className="ma-stat-num">{absentCount}</span>
          <span className="ma-stat-lbl">No Record</span>
        </div>
        <div className="ma-stat ma-stat-blue">
          <span className="ma-stat-num">{leaveCount}</span>
          <span className="ma-stat-lbl">On Leave</span>
        </div>
        <div className="ma-stat">
          <span className="ma-stat-num">{employees.length}</span>
          <span className="ma-stat-lbl">Total</span>
        </div>
      </div>

      {/* Search */}
      <input className="ma-search" type="text"
        placeholder="Search employee name or ID…"
        value={search} onChange={e => setSearch(e.target.value)} />

      {/* Employee list */}
      {loading ? (
        <div className="ma-loading">Loading…</div>
      ) : (
        <div className="ma-list">
          {filtered.map((emp, i) => {
            const rec    = getRecord(emp);
            const badge  = getStatusBadge(rec);
            return (
              <div key={i} className="ma-row">
                <div className="ma-emp-cell">
                  <div className="ma-av" style={{ background: getAvatarColor(emp.full_name) }}>
                    {getInitials(emp.full_name)}
                  </div>
                  <div className="ma-emp-info">
                    <div className="ma-emp-name">{emp.full_name}</div>
                    <div className="ma-emp-meta">{emp.employee_id} · {emp.location_name || '—'}</div>
                  </div>
                </div>

                <div className="ma-times">
                  {rec?.clock_in ? (
                    <>
                      <span className="ma-time-in">IN {formatTimeForInput(rec.clock_in)}</span>
                      {rec.clock_out && <span className="ma-time-out">OUT {formatTimeForInput(rec.clock_out)}</span>}
                    </>
                  ) : (
                    <span className="ma-no-time">—</span>
                  )}
                </div>

                <span className={`ma-status-badge ${badge.cls}`}>{badge.label}</span>

                <button className="ma-edit-btn"
                  onClick={() => setEditData({ employee: emp, record: rec || null })}>
                  {rec ? 'Edit' : '+ Add'}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {editData && (
        <EditModal
          employee={editData.employee}
          record={editData.record}
          date={date}
          onSave={handleSave}
          onClose={() => setEditData(null)}
        />
      )}
    </div>
  );
}